import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment';

const DepositMonitor = () => {
    // State Management
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchUser, setSearchUser] = useState('');
    const [searchDevice, setSearchDevice] = useState(''); 
    const [activeTab, setActiveTab] = useState('all'); 
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);

    // 1. Data Fetching Function
    const fetchLogs = useCallback(() => {
        setLoading(true);
        socketService.emit('fetch_deposit_monitor_logs', {
            page: currentPage,
            search: searchTerm,
            username: searchUser,
            deviceName: searchDevice,
            provider: activeTab === 'all' ? '' : activeTab 
        });
    }, [currentPage, searchTerm, searchUser, searchDevice, activeTab]);

    // 2. Socket Listeners
    useEffect(() => {
        if (!socketService.connected) socketService.connect();

        const handleDataReceive = (data) => {
            setLogs(data.logs || []);
            setTotalPages(data.totalPages || 1);
            setTotalRows(data.totalRows || 0);
            setLoading(false);
        };

        const handleRealtimeUpdate = (updatedLog) => {
            setLogs(prevLogs => {
                const exists = prevLogs.find(l => l._id === updatedLog._id);
                if (exists) {
                    return prevLogs.map(l => l._id === updatedLog._id ? updatedLog : l);
                } else {
                    if(activeTab === 'all' || updatedLog.provider === activeTab) {
                        return [updatedLog, ...prevLogs].slice(0, 50); 
                    }
                    return prevLogs;
                }
            });
        };

        const handleRealtimeNew = (newLog) => {
             if(activeTab === 'all' || newLog.provider === activeTab) {
                setLogs(prevLogs => [newLog, ...prevLogs].slice(0, 50));
             }
        };

        const handleRealtimeDelete = ({ id }) => {
            setLogs(prevLogs => prevLogs.filter(log => log._id !== id));
            setTotalRows(prev => Math.max(0, prev - 1));
        };

        const handleSmsLogResponse = (data) => {
            if(data.success) {
                console.log("Manual Sync Success:", data.message);
            } else {
                // এরর হলে এলার্ট দেখাতে পারেন
                alert(data.message);
            }
        };

        socketService.on('receive_deposit_monitor_logs', handleDataReceive);
        socketService.on('deposit_monitor_update', handleRealtimeUpdate);
        socketService.on('deposit_monitor_new', handleRealtimeNew);
        socketService.on('deposit_monitor_deleted', handleRealtimeDelete);
        socketService.on('sms_log_response', handleSmsLogResponse);

        fetchLogs();

        return () => {
            socketService.off('receive_deposit_monitor_logs', handleDataReceive);
            socketService.off('deposit_monitor_update', handleRealtimeUpdate);
            socketService.off('deposit_monitor_new', handleRealtimeNew);
            socketService.off('deposit_monitor_deleted', handleRealtimeDelete);
            socketService.off('sms_log_response', handleSmsLogResponse);
        };
    }, [fetchLogs, activeTab]);

    // 3. Handlers
    const handleSearch = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
    const handleUserSearch = (e) => { setSearchUser(e.target.value); setCurrentPage(1); };
    const handleDeviceSearch = (e) => { setSearchDevice(e.target.value); setCurrentPage(1); };
    const handleTabChange = (tab) => { setActiveTab(tab); setCurrentPage(1); };

    // ১. ভ্যালিডেশন ফাংশন (Nagad & bKash ফরম্যাট চেক করার জন্য)
    const isValidCashOutSMS = (message) => {
        if (!message) return false;
        // Nagad Pattern
        const isNagad = /Cash Out Received/i.test(message) && /TxnID:/i.test(message) && /Customer:/i.test(message);
        // bKash Pattern
        const isBkash = /Cash Out Tk/i.test(message) && /from/i.test(message) && /successful/i.test(message) && /TrxID/i.test(message);
        return isNagad || isBkash;
    };

    // =========================================================
    // 🔥 ২. Manual Push হ্যান্ডলার (ফিক্সড - নতুন ইভেন্ট সহ)
    // =========================================================
    const handleManualSync = (log) => {
        if (!log || !log.sms || !log.sms.message) return;

        const smsText = log.sms.message;
        let amount, customer, txnID, senderType;
        let formattedMessage = "";

        // Regex দিয়ে ডাটা বের করা
        const nagadMatch = smsText.match(/Amount:\s*Tk\s*([\d,]+\.?\d*).*Customer:\s*([\d*]+).*TxnID:\s*([A-Z0-9]+)/i);
        const bkashMatch = smsText.match(/Cash Out Tk\s*([\d,]+\.?\d*).*from\s*([\d*X]+).*TrxID\s*([A-Z0-9]+)/i);

        if (nagadMatch) {
            // --- NAGAD ---
            senderType = 'NAGAD';
            amount = parseFloat(nagadMatch[1].replace(/,/g, ''));
            customer = nagadMatch[2];
            txnID = nagadMatch[3];
            formattedMessage = `Cash Out | Amount: ${amount} Tk., A/C : ${customer} TxnID: ${txnID}`;

        } else if (bkashMatch) {
            // --- BKASH ---
            senderType = 'bKash';
            amount = parseFloat(bkashMatch[1].replace(/,/g, ''));
            customer = bkashMatch[2];
            txnID = bkashMatch[3];
            formattedMessage = `Cash Out | Amount: ৳${amount.toFixed(2)} number: ${customer} TxnID: ${txnID}`;

        } else {
            alert("❌ Could not parse Amount, Customer, or TxnID from the SMS!");
            return;
        }

        if(window.confirm(`Are you sure you want to manually create this App Log?\n\n"${formattedMessage}"`)) {
            
            // ১. 🔥 Optimistic Update: সার্ভারের জন্য অপেক্ষা না করে সাথে সাথে UI আপডেট
            setLogs(prevLogs => prevLogs.map(item => {
                if (item._id === log._id) {
                    return {
                        ...item,
                        status: 'matched', // স্ট্যাটাস Matched করে দেওয়া হলো
                        app: {
                            received: true,
                            packageName: senderType === 'NAGAD' ? 'com.konasl.nagad.agent' : 'com.bkash.businessapp', 
                            title: 'Manual Push',
                            message: formattedMessage,
                            receivedAt: new Date()
                        }
                    };
                }
                return item;
            }));

            // ২. 🔥 সার্ভারে নতুন ইভেন্ট পাঠানো (ID সহ)
            socketService.emit('manual_push_deposit_monitor', {
                username: log.username,
                senderID: senderType,
                message: formattedMessage,
                simno: 'Manual-Push',
                deviceName: log.deviceName,
                receivedAt: log.sms.receivedAt,
                depositMonitorId: log._id // ✅ এই ID দিয়ে সার্ভার স্পেসিফিক রো আপডেট করবে
            });
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    const MessageView = ({ title, message, time, isMissing, type }) => {
        if (isMissing) {
            return (
                <div className="postman-viewer text-center py-4" style={{ border: '2px dashed #ef4444', backgroundColor: '#fef2f2', opacity: 0.8 }}>
                    <span style={{ color: '#dc2626', fontSize: '11px', fontWeight: 'bold', letterSpacing: '1px' }}>❌ {type} NOT FOUND</span>
                </div>
            );
        }
        return (
            <div className="postman-viewer">
                {title && <div className="key mb-1">{title}</div>}
                <div style={{color: '#d4d4d4', whiteSpace: 'pre-wrap'}}>{message}</div>
                <div className="mt-2 text-end" style={{fontSize: '10px', color: '#60a5fa'}}>{moment(time).format('hh:mm:ss A')}</div>
            </div>
        );
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">💰 Deposit Monitor</h5>
                    <div className="d-flex gap-2">
                        <div className="btn-group shadow-sm">
                            <button className={`btn btn-sm ${activeTab === 'all' ? 'btn-primary' : 'btn-light'}`} onClick={() => handleTabChange('all')}>All</button>
                            <button className={`btn btn-sm ${activeTab === 'bkash' ? 'btn-primary' : 'btn-light'}`} onClick={() => handleTabChange('bkash')}>bKash</button>
                            <button className={`btn btn-sm ${activeTab === 'nagad' ? 'btn-primary' : 'btn-light'}`} onClick={() => handleTabChange('nagad')}>Nagad</button>
                        </div>
                    </div>
                </div>

                <div className="filter-card shadow-sm border-0 mb-4">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-3">
                            <label className="field-label">FILTER BY USER</label>
                            <input type="text" className="modern-input" placeholder="Enter Username..." value={searchUser} onChange={handleUserSearch} />
                        </div>
                        
                        <div className="col-md-3">
                            <label className="field-label">FILTER BY DEVICE</label>
                            <input type="text" className="modern-input" placeholder="Enter Device Name..." value={searchDevice} onChange={handleDeviceSearch} />
                        </div>

                        <div className="col-md-4">
                            <label className="field-label">SEARCH BY TrxID / AMOUNT</label>
                            <input type="text" className="modern-input" placeholder="Enter TrxID or Amount..." value={searchTerm} onChange={handleSearch} />
                        </div>

                        <div className="col-md-2">
                            <button className="btn btn-apply fw-bold w-100" onClick={fetchLogs}>Refresh Data</button>
                        </div>
                    </div>
                </div>

                <div className="table-container shadow-sm border-0 mb-3">
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4" style={{width: '22%'}}>SMS LOG</th>
                                    <th style={{width: '22%'}}>WITHOUT LOGIN</th>
                                    <th style={{width: '22%'}}>APP Notification (Main)</th>
                                    <th className="ps-4" style={{width: '20%'}}>Out Info</th>
                                    <th className="text-end pe-4" style={{width: '14%'}}>STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center py-5">Checking Deposits...</td></tr>
                                ) : logs.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center py-5 opacity-50">No Deposit Data Found</td></tr>
                                ) : (
                                    logs.map((log) => {
                                        const isMatched = log.status === 'matched';
                                        
                                        // বাটন দেখানোর শর্ত: Only SMS & Valid Nagad/bKash format
                                        const isSmsOnly = (log.status === 'only_sms' || (log.sms.received && !log.app.received && !log.withoutLogin.received));
                                        const showPushButton = isSmsOnly && isValidCashOutSMS(log.sms.message);

                                        const rowStyle = { verticalAlign: 'top', backgroundColor: isMatched ? '#f0fdf4' : '#fffbeb', borderLeft: isMatched ? '4px solid #22c55e' : '4px solid #f59e0b' };
                                        
                                        return (
                                            <tr key={log._id} style={rowStyle}>
                                                <td className="ps-4 pt-3 pb-3">
                                                    <div className="code-label mb-1">{log.sms.received ? log.sms.sender : 'NO SMS'}</div>
                                                    <MessageView message={log.sms.message} time={log.sms.receivedAt} isMissing={!log.sms.received} type="SMS" />
                                                </td>
                                                <td className="pt-3 pb-3">
                                                    {log.withoutLogin && log.withoutLogin.received ? (
                                                        <>
                                                            <div className="code-label mb-1 text-warning" style={{color: '#0219e9ff'}}>WITHOUT-LOGIN</div>
                                                            <MessageView message={log.withoutLogin.message} time={log.withoutLogin.receivedAt} isMissing={false} type="NO-LOGIN" />
                                                        </>
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-center h-100 opacity-25 pt-4"><span style={{fontSize: '11px', color: '#94a3b8', fontWeight: '600'}}>- NO DATA -</span></div>
                                                    )}
                                                </td>
                                                <td className="pt-3 pb-3">
                                                    <div className="code-label mb-1">{log.app.received ? log.app.packageName : 'NO APP DATA'}</div>
                                                    <MessageView title={log.app.title} message={log.app.message} time={log.app.receivedAt} isMissing={!log.app.received} type="APP" />
                                                </td>
                                                <td className="pt-3 pb-3 ps-4">
                                                    <div className="out-info-card">
                                                        <div className="mb-2"><div className="text-secondary small-label">AMOUNT</div><div className="amount-display">৳{log.amount.toFixed(2)}</div></div>
                                                        <div className="mb-2"><div className="text-secondary small-label">TRX ID</div><div className="trx-box">{log.trxID.startsWith('NO_TRX') ? <span className="text-muted">Waiting...</span> : log.trxID}</div></div>
                                                        <div className="device-footer"><div>📱 {log.deviceName}</div><div className="text-primary">👤 {log.username}</div></div>
                                                    </div>
                                                </td>
                                                <td className="text-end pe-4 pt-3 align-middle">
                                                    <div className="d-flex flex-column align-items-end gap-2">
                                                        {isMatched ? (
                                                            <>
                                                                <div className="d-flex align-items-center gap-1"><span className="badge bg-success bg-opacity-10 text-success border border-success px-3 py-2 rounded-pill" style={{fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px'}}>AUTO</span></div>
                                                                <div style={{fontSize: '11px', fontWeight: '700', color: '#166534'}}>✅ MATCHED</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="d-flex align-items-center gap-1"><span className="badge bg-warning bg-opacity-10 text-warning border border-warning px-3 py-2 rounded-pill" style={{fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px'}}>PENDING</span></div>
                                                                <div style={{fontSize: '11px', fontWeight: '700', color: '#b45309'}}>{isSmsOnly ? '⚠️ APP MISSING' : '⚠️ SMS MISSING'}</div>
                                                                
                                                                {showPushButton && (
                                                                    <button 
                                                                        className="btn btn-sm btn-outline-primary mt-1" 
                                                                        style={{fontSize: '10px', padding: '2px 8px'}}
                                                                        onClick={() => handleManualSync(log)}
                                                                    >
                                                                        📤 Manual Push
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                 {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center px-2 py-2 border-top bg-white mt-3 rounded-bottom">
                        <div className="text-muted small">Total <b>{totalRows}</b> Records | Page <b>{currentPage}</b> of <b>{totalPages}</b></div>
                        <nav>
                            <ul className="pagination pagination-sm m-0 gap-1">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}><button className="page-link shadow-none" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Prev</button></li>
                                {getPageNumbers().map((page) => (<li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}><button className="page-link shadow-none" onClick={() => setCurrentPage(page)}>{page}</button></li>))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}><button className="page-link shadow-none" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>Next</button></li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .btn-group .btn { font-weight: 600; font-size: 12px; padding: 6px 15px; }
                .btn-primary { background-color: #0284c7; border-color: #0284c7; }
                .btn-light { background-color: white; color: #64748b; border: 1px solid #e2e8f0; }
                .filter-card { background: #ffffff; padding: 15px; border-radius: 12px; }
                .field-label { font-size: 10px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 7px 10px; border-radius: 8px; font-size: 12px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 12px; transition: 0.3s; border: none; padding: 8px 0; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #f1f5f9; color: #475569; border-bottom: 2px solid #e2e8f0; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; text-align: left; }
                .custom-zebra-table td { border-bottom: 1px solid #edf2f7; }
                .code-label { font-size: 10px; font-weight: 700; color: #94a3b8; letter-spacing: 0.5px; text-transform: uppercase; }
                .out-info-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; max-width: 200px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
                .small-label { font-size: 9px; font-weight: 700; color: #94a3b8; margin-bottom: 2px; letter-spacing: 0.5px; }
                .amount-display { font-size: 18px; font-weight: 800; color: #059669; line-height: 1; }
                .trx-box { font-family: 'Courier New', monospace; font-weight: 700; background: #f1f5f9; padding: 4px 6px; border-radius: 4px; font-size: 12px; color: #334155; border: 1px solid #e2e8f0; }
                .device-footer { margin-top: 8px; padding-top: 6px; border-top: 1px dashed #e2e8f0; font-size: 13px; font-weight: 600; color: #64748b; display: flex; justify-content: space-between; }
                .postman-viewer { background-color: #212121; color: #d4d4d4; padding: 10px; border-radius: 6px; font-family: 'Consolas', 'Monaco', monospace; font-size: 11px; line-height: 1.4; overflow: hidden; border: 1px solid #333; max-width: 400px; }
                .key { color: #9cdcfe; font-weight: bold; } 
                .page-link { color: #475569; border: 1px solid #e2e8f0; border-radius: 6px !important; padding: 5px 12px; font-weight: 600; background: white; }
                .page-item.active .page-link { background-color: #0284c7; border-color: #0284c7; color: white; }
            `}</style>
        </Layout>
    );
};

export default DepositMonitor;